import { createRoute, redirect } from '@tanstack/react-router';
import { useState } from 'react';

// WorkOS integration
import { useWorkOSUrl } from './useWorkOSUrl';

// @mui material components
import Card from '@mui/material/Card';

// Material Dashboard 2 PRO React TS imports
import MDBox from 'src/components/md/MDBox';
import MDTypography from 'src/components/md/MDTypography';
import MDInput from 'src/components/md/MDInput';
import MDButton from 'src/components/md/MDButton';
import BasicLayout from 'src/md-layouts/authentication/components/BasicLayout';
import bgImage from 'src/assets/images/bg-sign-in-basic.jpeg';

import useAuth from 'src/api/auth/useAuth';
import useEmailPasswordLogIn from 'src/api/auth/useEmailPasswordLogIn';
import ErrorMessage from 'src/components/errorMessage/ErrorMessage';
import { CircularProgress } from '@mui/material';
import { Route as rootRoute } from 'src/pages/__root';
import { useNavigate } from '@tanstack/react-router';
import z from 'zod';

export const Route = createRoute({
  path: '/login',
  getParentRoute: () => rootRoute,
  validateSearch: z.object({
    redirect: z
      .string()
      .transform((href) => {
        if (!href) return undefined;

        const redirectHref =
          href.length > 2000 ? href.substring(0, href.indexOf('?')) : href;
        return redirectHref;
      })
      .optional(),
  }),
  beforeLoad: ({ context, search }) => {
    // Redirect if already authenticated
    if (context.auth.currentUser) {
      throw redirect({ to: search.redirect || '/' });
    }
  },
  component: LoginPage,
});

export function LoginPage() {
  const navigate = useNavigate();
  const { isRefetching, refetchUser } = useAuth();
  const { redirect } = Route.useSearch();

  const {
    mutate: emailPasswordLogInApi,
    isLoading: isAuthenticating,
    error,
  } = useEmailPasswordLogIn(async () => {
    await refetchUser();
    navigate({ to: redirect || '/' });
  });

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [errors, setErrors] = useState({ email: '', password: '' });

  // Initialize WorkOS URL fetch
  const {
    data: workOSUrlData,
    isLoading: isLoadingWorkOSUrl,
    error: workOSUrlError,
  } = useWorkOSUrl();

  const isDisable = isRefetching || isAuthenticating;

  const handleIdpLogin = () => {
    if (workOSUrlData?.authorizationUrl) {
      window.location.href = workOSUrlData.authorizationUrl;
    }
  };

  const validateInputs = () => {
    const newErrors = { email: '', password: '' };
    let isValid = true;

    if (!email) {
      newErrors.email = 'Email is required.';
      isValid = false;
    }

    if (!password) {
      newErrors.password = 'Password is required.';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleLogin = () => {
    if (!validateInputs()) return;
    emailPasswordLogInApi({ email, password });
  };

  const handleSubmit = (e: React.FormEvent<HTMLDivElement>) => {
    e.preventDefault();
    handleLogin();
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDTypography variant="h4" fontWeight="medium" color="white" mt={1}>
            Log in
          </MDTypography>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" onSubmit={handleSubmit}>
            <MDBox mb={2}>
              <MDInput
                type="email"
                label="Email"
                fullWidth
                value={email}
                disabled={isDisable}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEmail(e.target.value)
                }
                error={Boolean(errors.email)}
                helperText={errors.email}
              />
            </MDBox>
            <MDBox mb={2}>
              <MDInput
                type="password"
                label="Password"
                fullWidth
                value={password}
                disabled={isDisable}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setPassword(e.target.value)
                }
                error={Boolean(errors.password)}
                helperText={errors.password}
              />
            </MDBox>
            <MDBox mt={4} mb={1}>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                disabled={isDisable}
                type="submit"
              >
                {isAuthenticating ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'log in'
                )}
              </MDButton>
            </MDBox>
            <ErrorMessage error={error} mt={2} />
          </MDBox>
          {workOSUrlData?.authorizationUrl && (
            <MDBox mt={3}>
              <MDBox mt={2} mb={2} textAlign="center">
                <MDTypography variant="button" color="text">
                  or
                </MDTypography>
              </MDBox>
              <MDButton
                variant="gradient"
                color="info"
                fullWidth
                disabled={isLoadingWorkOSUrl}
                onClick={handleIdpLogin}
              >
                {isLoadingWorkOSUrl ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  'Log in with SSO'
                )}
              </MDButton>
              <ErrorMessage error={workOSUrlError} mt={2} />
            </MDBox>
          )}
        </MDBox>
      </Card>
    </BasicLayout>
  );
}
